import React from "react"
import { GatsbyImage } from "gatsby-plugin-image"
import { twMerge } from "tailwind-merge"
import { H6, Caption } from "../el/typography"
import { PillLink, PageButton } from "../el/button"
import { CheckCircleIcon } from "@heroicons/react/20/solid"
import { CheckIcon } from "@heroicons/react/20/solid"
import { ChevronRightIcon } from "@heroicons/react/20/solid"
/*
BASIC USAGE

<Block [lighttop, lightbottom, darktop, darkbottom, whitefull, lightfull, darkfull]>
  <Header [[normal, snug, tight], [center]]>header items like H2</Header>
  <Section [[lighttop, lightbottom, darktop, darkbottom], 
          (if dark or light full on block then card is blank)
          [normal, snug, tight],
          [[general[flex, [grid[gap]]],onecard, twocad, threecard, iconlist]>
          (Card has grid-cols-8, if gap then gap-8)

    <CardBody className="lg:col-span-?(up to 8)"> 
      text items...
    </CardBody>

    (and/or)

    <CardImage gatsbyImageData={} [cover] className="col-span-?(up to 8)"/>

  </Section>
</Block>
*/

// ============MAIN BLOCK============
const Block = props => {
  return (
    <div
      className={twMerge([
        props.theme === "whitefull" && "bg-slate-50",
        props.theme === "lightfull" && "bg-slate-200",
        props.theme === "darkfull" && "bg-slate-900",
        props.theme === "lighttop" && "bg-slate-200",
        props.theme === "darktop" && "bg-slate-900",
        props.theme === "darkbottom" && "bg-slate-50",
        props.theme === "lightbottom" && "bg-slate-50",
      ])}
    >
      {/* {props.children} */}
      {React.Children.map(props.children, child => {
        return React.cloneElement(child, { theme: props.theme })
      })}
    </div>
  )
}
// ============HEADER============
const Header = props => {
  return (
    <div
      className={twMerge([
        props.center && "text-center",
        props.normal && "pt-12 sm:pt-16 xl:pt-20",
        props.snug && "pt-3 sm:pt-6 xl:pt-10",
        props.tight && "pt-0.5 sm:pt-2 xl:pt-4",
        props.className,
      ])}
    >
      <div className="mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8">
        {props.children}
      </div>
    </div>
  )
}
// ============SECTION============
const Section = props => {
  return (
    <div
      className={twMerge([
        props.noheader ? "mt-0" : "mt-8 sm:mt-12",
        props.theme === "lighttop" && "bg-slate-50",
        props.theme === "darktop" && "bg-slate-50",
        props.theme === "darkbottom" && "bg-slate-900",
        props.theme === "lightbottom" && "bg-slate-200",
        props.normal && "pb-16 sm:pb-20 xl:pb-28",
        props.snug && "pb-8 sm:pb-12 xl:pb-16",
        props.tight && "pb-3 sm:pb-6 xl:pb-8",
        props.className,
      ])}
    >
      <div className="relative">
        <div
          className={twMerge([
            "absolute inset-0 h-5/6 lg:h-2/3",
            props.theme === "lighttop" && "bg-slate-200",
            props.theme === "darktop" && "bg-slate-900",
            props.theme === "darkbottom" && "bg-slate-50",
            props.theme === "lightbottom" && "bg-slate-50",
          ])}
        ></div>
        <div className="relative mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8">
          {props.general && (
            <div
              className={twMerge([
                props.flex && "lg:flex lg:items-center lg:justify-between",
                props.grid && "lg:grid lg:grid-cols-8",
                props.grid && props.gap && "gap-8",
              ])}
            >
              {props.children}
            </div>
          )}
          {props.onecard && (
            <div className="mx-auto max-w-2xl overflow-hidden rounded-lg shadow-lg lg:grid lg:max-w-none lg:grid-cols-8">
              {props.children}
            </div>
          )}
          {props.twocard && (
            <div className="mx-auto grid max-w-md gap-5 lg:max-w-5xl lg:grid-cols-2 ">
              {props.children}
            </div>
          )}
          {props.threecard && (
            <div className="mx-auto grid max-w-lg gap-5 lg:max-w-none lg:grid-cols-3">
              {props.children}
            </div>
          )}
          {props.fourcard && (
            <div className="mx-auto grid max-w-lg gap-3 lg:max-w-none lg:grid-cols-4">
              {props.children}
            </div>
          )}
          {props.iconlist && (
            <ul className="md:grid md:grid-cols-2 md:gap-x-8 md:gap-y-10">
              {props.children}
            </ul>
          )}
          {props.cardgroup && (
            <div className="relative lg:grid lg:grid-cols-7">
              {props.children}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
// ============SINGLE CARD============
const Card = props => {
  return (
    <div
      className={twMerge([
        "flex flex-col overflow-hidden rounded-lg shadow-lg",
        props.className,
      ])}
    >
      <div className={twMerge(["shrink-0", props.pill && "relative bg-white"])}>
        <GatsbyImage
          alt={props.alt}
          image={props.gatsbyImageData}
          className={twMerge([
            props.imageHeight
              ? "h-full w-auto object-cover"
              : "h-64 w-full object-cover",
            props.twocard && "h-64",
            props.threecard && "h-48",
          ])}
          imgClassName="rounded-t-lg"
        />
        {props.pill && (
          <PillLink
            color={props.pillcolor}
            className="absolute left-4 top-6 shadow-md"
            to={props.pillto}
          >
            {props.pilltitle}
          </PillLink>
        )}
      </div>
      {props.children}
    </div>
  )
}

// ============CARD COMPONENTS============
const CardWrap = props => {
  return (
    <div
      className={twMerge([
        "flex flex-1 flex-col justify-between bg-white",
        props.snug && "bg-white px-6 pb-8 pt-6 sm:p-10 sm:pt-6",
        props.loose && "p-6",
      ])}
    >
      <div className="flex-1">{props.children}</div>
      {props.footer && (
        <div className="mt-6 flex items-center">{props.footer}</div>
      )}
    </div>
  )
}
const CardBody = props => {
  return (
    <div className={twMerge(["bg-white px-6 py-8 lg:p-12", props.className])}>
      {props.children}
    </div>
  )
}

const CardImage = props => {
  return (
    <div
      className={twMerge([
        "relative",
        props.cover ? "" : "flex items-center justify-center p-6 pb-10 lg:p-8",
        props.className,
      ])}
    >
      <GatsbyImage
        alt={props.alt}
        image={props.gatsbyImageData}
        className={twMerge([
          "object-cover",
          props.cover ? "h-full w-full" : "h-auto w-full",
        ])}
        imgClassName={twMerge([
          props.fullImage
            ? props.square
              ? "rounded-none"
              : "rounded-lg"
            : "rounded-b-lg lg:rounded-b-none lg:rounded-r-lg",
        ])}
      />
      {props.caption ? (
        <Caption className="absolute inset-x-0 bottom-0 bg-white px-6 pb-2 pt-0.5 lg:px-8">
          {props.caption}
        </Caption>
      ) : (
        ``
      )}
    </div>
  )
}
// ============CARD BODY SUB COMPONENTS============
const PillRow = props => {
  return (
    <p className="flex flex-wrap items-center justify-start">
      {props.children}
    </p>
  )
}

// ============CARD SUB HEADING---------------
const CardSub = props => {
  return (
    <div className="mt-8">
      <div className="block sm:flex sm:items-center">
        <H6 className="mt-0 max-w-md shrink-0 bg-white pr-4">{props.title}</H6>
        <div className="hidden flex-1 border-t-2 border-slate-200 sm:block"></div>
      </div>
      {props.children}
    </div>
  )
}
// ============CARD LIST================
const CardList = props => {
  return (
    <div className="mt-8">
      <div className="flex items-center">
        <h4 className="shrink-0 bg-white pr-4 text-sm font-semibold uppercase leading-5 tracking-wider text-blue-600">
          {props.title}
        </h4>
        <div className="flex-1 border-t-2 border-slate-200"></div>
      </div>
      <ul className="mt-8 lg:grid lg:grid-cols-2 lg:gap-x-8 lg:gap-y-5">
        {props.children}
      </ul>
    </div>
  )
}
const CardLi = props => {
  return (
    <li className="mb-4 flex items-start last-of-type:mb-0 lg:col-span-1">
      <div className="shrink-0">
        {props.circle && (
          <CheckCircleIcon className="h-5 w-5 text-emerald-500" />
        )}
        {!props.check && !props.circle && (
          <ChevronRightIcon className="h-5 w-5 text-blue-500" />
        )}
        {props.check && <CheckIcon className="h-6 w-6 text-emerald-500" />}
      </div>
      <p className="ml-3 text-sm leading-5 text-slate-500">{props.children}</p>
    </li>
  )
}

// =============ICON LIST===============

const IconList = props => {
  return (
    <li className="mt-6 md:mt-0">
      <div className="flex">
        <div className="shrink-0">
          <div
            className={twMerge([
              "listIconStyles",
              "flex h-12 w-12 items-center justify-center rounded-md text-white",
              props.color === "gray" && "bg-slate-500 text-slate-100",
              props.color === "pink" && "bg-pink-500 text-pink-100",
              props.color === "red" && "bg-rose-500 text-rose-100",
              props.color === "orange" && "bg-orange-500 text-orange-100",
              props.color === "yellow" && "bg-amber-500 text-amber-100",
              props.color === "green" && "bg-emerald-500 text-emerald-100",
              props.color === "teal" && "bg-teal-500 text-teal-100",
              props.color === "blue" && "bg-blue-500 text-blue-100",
              props.color === "purple" && "bg-violet-500 text-violet-100",
              props.color === "indigo" && "bg-indigo-500 text-indigo-100",
              props.className,
            ])}
          >
            {props.svg}
          </div>
        </div>

        <div className="ml-4">{props.children}</div>
      </div>
    </li>
  )
}

// =================CARD GROUP================
const CardGroupLeft = props => {
  return (
    <div className="mx-auto max-w-md lg:col-start-1 lg:col-end-3 lg:row-start-2 lg:row-end-3 lg:mx-0 lg:max-w-none">
      <div className="flex h-full flex-col overflow-hidden rounded-lg shadow-lg lg:rounded-none lg:rounded-l-lg">
        <div className="flex flex-1 flex-col">
          <div className="bg-white">
            <GatsbyImage
              alt={props.alt}
              image={props.gatsbyImageData}
              className="h-auto w-full object-cover"
              imgClassName="rounded-t-lg lg:rounded-t-none lg:rounded-tl-lg"
            />
          </div>

          <div className="flex flex-1 flex-col justify-between border-t-2 border-slate-100 bg-slate-50 p-6 sm:p-10 lg:p-6 xl:p-10">
            {props.children}
            <div className="mt-8">
              <PageButton to={props.to} white>
                {props.linktitle}
              </PageButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const CardGroupRight = props => {
  return (
    <div className="mx-auto mt-10 max-w-md lg:col-start-6 lg:col-end-8 lg:row-start-2 lg:row-end-3 lg:m-0 lg:max-w-none">
      <div className="flex h-full flex-col overflow-hidden rounded-lg shadow-lg lg:rounded-none lg:rounded-r-lg">
        <div className="flex flex-1 flex-col">
          <div className="bg-white">
            <GatsbyImage
              alt={props.alt}
              image={props.gatsbyImageData}
              className="h-auto w-full object-cover"
              imgClassName="rounded-t-lg lg:rounded-t-none lg:rounded-tr-lg"
            />
          </div>
          <div className="flex flex-1 flex-col justify-between border-t-2 border-slate-100 bg-slate-50 p-6 sm:p-10 lg:p-6 xl:p-10">
            {props.children}
            <div className="mt-8">
              <PageButton to={props.to} white>
                {props.linktitle}
              </PageButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
const CardGroupMain = props => {
  return (
    <div className="mx-auto mt-10 max-w-lg lg:col-start-3 lg:col-end-6 lg:row-start-1 lg:row-end-4 lg:mx-0 lg:mt-0 lg:max-w-none">
      <div className="relative z-10 rounded-lg shadow-xl">
        <div
          className={`pointer-events-none absolute inset-0 rounded-lg border-2 ${props.bordercolor} z-20`}
        ></div>
        <div className="absolute inset-x-0 top-0 z-40 translate-y-px transform">
          <div className="flex -translate-y-1/2 transform justify-center">
            <span
              className={`inline-flex rounded-full ${props.pillcolor} px-4 py-1 text-sm font-semibold uppercase leading-5 tracking-wider text-white`}
            >
              {props.pill}
            </span>
          </div>
        </div>
        <div className="rounded-t-lg bg-white">
          <GatsbyImage
            alt={props.alt}
            image={props.gatsbyImageData}
            className="h-auto w-full rounded-t-lg object-cover"
            imgClassName="rounded-t-lg"
          />
        </div>
        <div className="rounded-b-lg border-t-2 border-slate-100 bg-slate-50 px-6 pb-8 pt-10 sm:px-10 sm:py-10">
          {props.children}
          <div className="mt-10">
            <PageButton to={props.to} dark>
              {props.linktitle}
            </PageButton>
          </div>
        </div>
      </div>
    </div>
  )
}
export {
  Block,
  Header,
  Section,
  CardBody,
  CardImage,
  CardSub,
  CardList,
  CardLi,
  IconList,
  Card,
  CardWrap,
  PillRow,
  CardGroupLeft,
  CardGroupRight,
  CardGroupMain,
}
